import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useThorView } from '@aviv-dxp/react-thor';
import { useNavigate } from '@aviv-unified-frontend/runtime/router';
import { Box } from '@gemini/core';
import { ListIcon } from '@gemini/icons';
import { Button } from '@gemini/ui';
import { HouseNumbersList } from 'src/components/HouseNumbersList';
import { MapBoxMarker } from '@/components/MapBoxMarker/MapBoxMarker';
import { MapBoxPopup } from '@/components/MapBoxPopup/MapBoxPopup';
import { MapLegend } from '@/components/MapLegend';
import { MapTileError } from '@/components/MapTileError';
import { AgencyCardMap } from '@/components/MicroFrontEnds/AgencyCard/AgencyCardMap';
import { PageSpinner } from '@/components/PageSpinner';
import { MediumCta } from '@/entities/enums/MediumCta';
import { getMediumAttribute } from '@/helpers/tracking/tracking';
import { MapEvents, useMap } from '@/hooks/useMap/useMap';
import { MapContext } from '@/hooks/useMapContext/useMapContext';
import { useMobileViewDetect } from '@/hooks/useMobileViewDetect/useMobileViewDetect';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
import { getPageTitle, useTrackingProps } from '@/hooks/useTrackingProps/useTrackingProps';
import { PriceCardMap } from '../PriceCardMap';
import { AgencyMapPin } from './AgencyMapPin';
import { MapContainer } from './MapContainer';
const MapComponent = () => {
    const { sidebarOpen, setSidebarOpen, pricePageProps: { pricePageContext: { place }, }, } = usePageLayoutContext();
    const navigate = useNavigate();
    const { t } = useShellTranslation();
    const [houseNumbersData, setHouseNumbersData] = useState(undefined);
    const [agencyCardData, setAgencyCardData] = useState(undefined);
    const mapData = useMap(place);
    const view = useThorView();
    const viewProps = useTrackingProps();
    const { isMobileView } = useMobileViewDetect();
    const houseNumbersPopupRef = useRef(null);
    const agencyCardPopupRef = useRef(null);
    const showBuildingHouseNumbersCallBack = useCallback((e) => {
        setHouseNumbersData(e);
    }, []);
    const showAgencyCardCallBack = useCallback((e) => {
        if (e.agencyId && e.lngLat) {
            setAgencyCardData({ agencyId: e.agencyId, lngLat: e.lngLat });
        }
        else {
            if (isMobileView) {
                setAgencyCardData(undefined);
            }
        }
    }, [isMobileView]);
    const navigateToUrlCallBack = useCallback((e) => {
        navigate(e.url);
    }, [navigate]);
    useEffect(() => {
        mapData.map?.on(MapEvents.NAVIGATE_TO_URL, navigateToUrlCallBack);
        mapData.map?.on(MapEvents.SHOW_BUILDING_HOUSE_NUMBERS, showBuildingHouseNumbersCallBack);
        mapData.map?.on(MapEvents.SHOW_AGENCY_CARD, showAgencyCardCallBack);
        return () => {
            mapData.map?.off(MapEvents.NAVIGATE_TO_URL, navigateToUrlCallBack);
            mapData.map?.off(MapEvents.SHOW_BUILDING_HOUSE_NUMBERS, showBuildingHouseNumbersCallBack);
            mapData.map?.off(MapEvents.SHOW_AGENCY_CARD, showAgencyCardCallBack);
        };
    }, [
        mapData,
        navigateToUrlCallBack,
        navigate,
        showBuildingHouseNumbersCallBack,
        showAgencyCardCallBack,
    ]);
    const handleButtonSwitch = () => {
        view({
            viewProps: {
                ...viewProps,
                av_medium: getMediumAttribute(sidebarOpen ? MediumCta.SWITCH_MAP_VIEW : MediumCta.SWITCH_INFO_VIEW, place.type, place.type),
                // av_pagetitle is calculated with the future value of listView
                av_pagetitle: getPageTitle(place, isMobileView, !sidebarOpen),
            },
        });
        setSidebarOpen((sideBarWasOpen) => !sideBarWasOpen);
    };
    const mapBottom = isMobileView ? '265px' : '132px';
    return (_jsx(MapContext.Provider, { value: mapData, children: _jsx(MapContext.Consumer, { children: ({ isMapLoaded }) => (_jsxs(Box, { flexGrow: "1", width: "100%", height: "100%", id: "map-component", position: "relative", overflow: "hidden", children: [_jsx(MapTileError, {}), !sidebarOpen && isMobileView ? (_jsx(Box, { position: "absolute", top: "spacing.8", left: {
                            'breakpoint.base': 'spacing.16',
                            'breakpoint.sm': 'spacing.32',
                        }, zIndex: "zIndex.1", children: _jsx(Button, { icon: ListIcon, iconPosition: "start", variant: "primary-inverted", onPress: () => handleButtonSwitch(), children: t('button_switch.info_view') }) })) : undefined, _jsxs(Box, { position: "absolute", top: "0", bottom: "0", flexGrow: "1", height: "100%", width: "100%", display: "flex", flexDirection: "column", transitionProperty: "usafe", children: [_jsxs(Box, { "data-testid": "map-legend", position: "absolute", bottom: "0", height: sidebarOpen && isMobileView ? '112px' : 'auto', width: "100%", zIndex: "zIndex.1", padding: "spacing.16", transform: sidebarOpen && isMobileView
                                    ? 'translateY(100%)'
                                    : 'translateY(0%)', transitionDuration: !sidebarOpen && isMobileView ? 'motionDuration.300' : 'unset', transitionDelay: !sidebarOpen && isMobileView ? 'motionDuration.300' : 'unset', transitionTimingFunction: "motionEasing.ease", backgroundColor: "color.background.default", color: "color.content.default.default", display: "flex", flexDirection: "column", gap: "spacing.8", children: [!sidebarOpen &&
                                        isMobileView &&
                                        (agencyCardData ? (_jsx(AgencyCardMap, { id: agencyCardData.agencyId })) : (_jsxs(_Fragment, { children: [_jsx(Box, { textAlign: "center", children: _jsx(PriceCardMap, {}) }), _jsx(MapLegend, {})] }))), !isMobileView && _jsx(MapLegend, {})] }), _jsxs(Box, { position: "absolute", top: "0", bottom: !sidebarOpen && isMobileView ? '112px' : '52px', width: "100%", height: `calc(100vh - ${mapBottom})`, transform: sidebarOpen && isMobileView
                                    ? 'translateY(-100%)'
                                    : 'translateY(0%)', transitionDuration: "motionDuration.300", transitionTimingFunction: "motionEasing.ease", children: [!isMapLoaded && _jsx(PageSpinner, {}), houseNumbersData && (_jsx(MapBoxPopup, { lngLat: houseNumbersData.lngLat, containerRef: houseNumbersPopupRef })), _jsx(Box, { as: "div", ref: houseNumbersPopupRef, children: houseNumbersData && (_jsx(HouseNumbersList, { currentPlace: place, places: houseNumbersData.houseNumbers })) }), !isMobileView && agencyCardData && (_jsx(MapBoxMarker, { lngLat: agencyCardData.lngLat, containerRef: agencyCardPopupRef })), _jsx(Box, { as: "div", ref: agencyCardPopupRef, children: !isMobileView && agencyCardData && (_jsx(AgencyCardMap, { id: agencyCardData.agencyId })) }), _jsx(AgencyMapPin, { map: mapData.map, agencyPoints: mapData.agencyPoints }), _jsx(MapContainer, {})] })] })] })) }) }));
};
export default MapComponent;
